import Router, { useRouter } from 'next/router'
import * as React from 'react'
import { BaseRouter } from '@/lib/interface/event'
import { eventLogProducer } from './event-logger'
import { Action } from '@@/action_pb'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'

NProgress.configure({
  minimum: 0.3,
  easing: 'ease',
  speed: 800,
  showSpinner: false,
})

export interface IBeforePopStateHooks {
  from: string // url, not asPath
  callback: () => Promise<boolean>
}

class RouteEvents {
  beforePopState: Map<string, () => Promise<boolean>> = new Map()
}

export const RouteEventContext = React.createContext<RouteEvents>(new RouteEvents())

const RouteEventHandler = () => {
  const router = useRouter()
  const currentPath = React.useMemo(() => ({ asPath: router.asPath, url: router.route }), [router.asPath, router.route])
  const routeEvents = React.useContext(RouteEventContext)
  let durationTimeStemp = Date.now()
  React.useEffect(() => {
    let location: BaseRouter | undefined = undefined
    const target = {
      route: Router.route,
      pathname: Router.pathname,
      asPath: Router.asPath,
      query: { ...Router.query },
    }
    location = {
      route: '',
      pathname: '',
      asPath: '',
      query: {},
    }
    eventLogProducer.push({
      location: target,
      target,
      action: Action.PAGEEVENT,
      ...(Router.query.entry ? { extra: { entry: Router.query.entry as string } } : {}),
    })
    const handleRouteChangeStart = () => {
      location = {
        route: Router.route,
        pathname: Router.pathname,
        asPath: Router.asPath,
        query: { ...Router.query },
      }
      const leftTime = Date.now() - durationTimeStemp
      eventLogProducer.push({
        location,
        target,
        action: Action.LEAVEPAGE,
        extra: {
          duration: String(leftTime),
        },
      })
    }

    const handleRouteChangeComplete = () => {
      const target = {
        route: Router.route,
        pathname: Router.pathname,
        asPath: Router.asPath,
        query: { ...Router.query },
      }
      if (!!location) {
        eventLogProducer.push({
          location,
          target,
          action: Action.PAGEEVENT,
        })
      }
      durationTimeStemp = Date.now()
      location = undefined
    }
    Router.events.on('routeChangeStart', handleRouteChangeStart)
    Router.events.on('routeChangeComplete', handleRouteChangeComplete)
    Router.events.on('routeChangeStart', () => NProgress.start())
    Router.events.on('routeChangeComplete', () => NProgress.done())
    Router.events.on('routeChangeError', () => NProgress.done())

    return () => {
      Router.events.off('routeChangeStart', handleRouteChangeStart)
      Router.events.off('routeChangeComplete', handleRouteChangeComplete)
      Router.events.off('routeChangeStart', () => NProgress.start())
      Router.events.off('routeChangeComplete', () => NProgress.offe())
      Router.events.off('routeChangeError', () => NProgress.done())
    }
  }, [])
  React.useEffect(() => {
    Router.beforePopState((params: { url: string; as?: string; asPath?: string }) => {
      const { url, as, asPath } = params
      const registeredCallBack = routeEvents.beforePopState.get(router.route)
      if (registeredCallBack) {
        window.history.replaceState({}, '', currentPath.asPath)
        registeredCallBack().then((value) => {
          if (value) router.replace(url, as || asPath || '')
        })
        return false
      } else {
        return true
      }
    })
  }, [currentPath])
  return null
}

export const RouteEventProvider: React.FC<React.PropsWithChildren<any>> = ({ children }) => {
  const routeEvents = new RouteEvents()
  return (
    <RouteEventContext.Provider value={routeEvents}>
      <RouteEventHandler />
      {children}
    </RouteEventContext.Provider>
  )
}
